import * as React from 'react'
import { Link } from "react-router-dom"
import { Divider, Table, TableContainer, TableHead, TableBody, TableRow, TableCell, Paper, TableSortLabel, IconButton, Menu, MenuItem } from '@mui/material'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import { styled } from '@mui/system'
import ConfirmDialog from 'components/ConfirmDialog'
import { formatCurrency, formatString, formatDate } from 'utils/formatters'

// Data row styling
const DataRow = styled(TableRow)(({ theme }) => ({
  /*[`& th:first-of-type`]: {
    position: 'sticky',
    left: 0,
    minWidth: '200px',
  },*/
  [`thead`]: {
    zIndex: 3,
  },
  [`thead & th`]: {
    textWrap: 'nowrap',
  },
}))

// Data columns array
const columns = [
  {
    id: 'customerName',
    label: 'Customer',
    numeric: false,
    sortable: true,
  },
  {
    id: 'orderNo',
    label: 'Order No.',
    numeric: true,
    sortable: true,
  },
  {
    id: 'mealsNo',
    label: '# Meals',
    numeric: true,
    sortable: true,
  },
  {
    id: 'wrapsNo',
    label: '# Wraps',
    numeric: true,
    sortable: true,
  },
  {
    id: 'total',
    label: 'Total',
    numeric: true,
    sortable: true,
  },
  {
    id: 'deliveryDate',
    label: 'First Delivery Date',
    numeric: true,
    sortable: true,
  },
  {
    id: 'interval',
    label: 'Interval',
    numeric: true,
    sortable: false,
  },
  {
    id: 'nextDeliveryDate',
    label: 'Next Delivery Date',
    numeric: true,
    sortable: true,
  },
]

/**
 * Schedule component
 */
const Schedule = ({ order, editSchedule, deleteSchedule }) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null)
  
  const handleMenuOpen = (e) => {
    setMenuAnchorEl(e.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  const handleEditAction = (e) => {
    handleMenuClose()
    editSchedule()
  }

  const handleDeleteAction = (e) => {
    handleMenuClose()
    deleteSchedule()
  }

  return (
    <DataRow hover>
      <TableCell component="th" sx={{ bgcolor: order.type }}>
        {order.customerName}
      </TableCell>
      <TableCell align="right">{order.orderNo}</TableCell>
      <TableCell align="right">{order.mealsNo}</TableCell>
      <TableCell align="right">{order.wrapsNo}</TableCell>
      <TableCell align="right" sx={{ fontWeight: 'fontWeightBold', color: 'success.main' }}>
        {formatCurrency(order.total, { fromCents: true })}
      </TableCell>
      <TableCell align="right">{formatDate(order.deliveryDate)}</TableCell>
      <TableCell align="right">{formatString(order.interval)}</TableCell>
      <TableCell align="right">{formatDate(order.nextDeliveryDate)}</TableCell>
      <TableCell align="right" sx={{ py: 0 }}>
        <IconButton onClick={handleMenuOpen}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: { sx: { minWidth: 150 } },
            list: { component: 'div' }
          }}
        >
          <MenuItem component={Link} onClick={handleEditAction}>Edit</MenuItem>
          <Divider key="menu-divider" />
          <MenuItem key="menu-delete" component={Link} onClick={handleDeleteAction} sx={{ color: 'error.main' }}>Delete</MenuItem>
        </Menu>
      </TableCell>
    </DataRow>
  )
}

/**
 * ScheduleList component for displaying scheduled order list
 */
const ScheduleList = ({ orders, editSchedule, deleteSchedule }) => {
  const [sort, setSort] = React.useState({ column: 'nextDeliveryDate', order: 'asc' })
  const [scheduleToDelete, setScheduleToDelete] = React.useState(null)
  const [confirmOpen, setConfirmOpen] = React.useState(false)

  // Sort function
  const sortBy = (column) => {
    column = column === 'customer' ? 'type' : column
    setSort({
      column: column,
      order: sort.column === column && sort.order === 'asc' ? 'desc' : 'asc',
    })
  }

  // Sorting data
  orders.sort((a, b) => {
    if (a[sort.column] < b[sort.column]) return sort.order === 'asc' ? -1 : 1
    if (a[sort.column] > b[sort.column]) return sort.order === 'asc' ? 1 : -1
    return 0
  })

  // Handles delete action
  const handleDeleteAction = (order) => {
    setScheduleToDelete(order)
    setConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (scheduleToDelete) deleteSchedule(scheduleToDelete)
    setConfirmOpen(false)
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined" sx={{ my: 6, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <DataRow>
              {columns.map((column) => (
                <TableCell key={column.id} align={column.numeric ? 'right' : 'left'}>
                  <TableSortLabel
                    active={sort.column === column.id}
                    direction={sort.column === column.id ? sort.order : 'asc'}
                    onClick={() => sortBy(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>&nbsp;</TableCell>
            </DataRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <Schedule key={order._id} order={order} editSchedule={() => editSchedule(order)} deleteSchedule={() => handleDeleteAction(order)} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={confirmOpen}
        message={scheduleToDelete ? `Are you sure you want to delete the schedule for ${scheduleToDelete.orderNo}?` : ''}
        onConfirm={handleConfirmDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  )
}

export default ScheduleList
